/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CollectionsModule
 */

import { Collection } from 'ajs/modules/data-model/factories/collection.factory';
import { Pool } from 'items/pool.item.factory';
import { Pool as PoolObjectType } from 'object-types';

const poolInventoryDataSources = {
    inventory: {
        source: 'InventoryCollDataSource',
        transformer: 'InventoryDataTransformer',
        transport: 'ListDataTransport',
        fields: [
            'config',
            'health',
            'runtime',
            'alert',
        ],
    },
    'collection-metrics': {
        source: 'CollMetricsCollDataSource',
        transformer: 'CollMetricsDataTransformer',
        transport: 'CollMetricsDataTransport',
        fields: [
            'l4_server.avg_bandwidth',
            'l4_server.max_open_conns',
            'l4_server.avg_total_rtt',
            'l7_server.avg_complete_responses',
            'l7_server.avg_error_responses',
            'l7_server.pct_response_errors',
        ],
        dependsOn: 'config',
    },
};

export type TPoolCollection = typeof PoolCollection;

/**
 * @description Pool collection
 * @author Alex Malitsky, Zhiqian Liu, Nisar Nadaf
 */
export class PoolCollection extends Collection {
    public static ngDependencies = [
        Pool,
        'defaultValues',
    ];

    constructor(args = {} as any) {
        const { params = {}, ...restArgs } = args;
        const extendedArgs = {
            objectName: 'pool',
            objectType: 'Pool',
            windowElement: 'lazy-load',
            isStatic: false,
            searchFields: [
                'name',
                'addr',
            ],
            ...restArgs,
            params: {
                includeName_: true,
                join: 'application_persistence_profile_ref',
                ...params,
            },
        };

        const { objectName } = extendedArgs;

        // if the object name is pool-inventory, load by the inventory API
        // otherwise use default data source settings to load by the list (pool) API
        if (objectName === 'pool-inventory') {
            extendedArgs.allDataSources = poolInventoryDataSources;
            extendedArgs.defaultDataSources = 'inventory';
        }

        super(extendedArgs);

        this.itemClass_ = this.getNgDependency(Pool);
        this.serverDefaultsOverride_ = {
            certificate: undefined,
            servers: [],
            networks: [],
            autoscale_policy: undefined,
            x509_cert: [],
            max_conn_rate_per_server: undefined,
        };
    }

    public setDefaultServerPortDefault(port = this.getDefaultServerPort()): void {
        this.setDefaultItemConfigProps({
            default_server_port: port,
        });
    }

    private getDefaultServerPort(): number {
        const defaultValues = this.getNgDependency('defaultValues');

        return defaultValues.getDefaultItemConfigByType(PoolObjectType).default_server_port;
    }
}
